import React, { useState, useEffect } from 'react';
import SessionRow from './SessionRow';
import LiquidFillLoader from '../../../shared/components/ui/LiquidFillLoader';
import { DEFAULT_COLUMNS, AVAILABLE_COLUMNS } from '../config/sessionColumns';
import { TABLE_STYLES } from '../styles/dataTableStyles';

const SessionsTable = ({
  sessions,
  loading,
  loadingMore = false,
  error,
  totalSessions,
  hasMore = false,
  onLoadMore,
  hasSearchFilter,
  searchTerm,
  onSearch,
  searchPlaceholder = 'Search by session ID or user ID...',
  sessionIdFromUrl,
  columnConfig
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const config = columnConfig || DEFAULT_COLUMNS;
  const visibleOrder = (config.order || []).filter(key => (config.enabled || []).includes(key));
  const styles = TABLE_STYLES;

  useEffect(() => {
    if (sessionIdFromUrl && sessions.length > 0 && !loading) {
      const matchingSession = sessions.find(s =>
        s.sessionId === sessionIdFromUrl ||
        s.sessionId?.toLowerCase() === sessionIdFromUrl.toLowerCase()
      );
      if (matchingSession && !expandedRows.has(matchingSession.sessionId)) {
        setExpandedRows(new Set([matchingSession.sessionId]));
      }
    }
  }, [sessionIdFromUrl, sessions, loading, expandedRows]);

  const toggleRow = (sessionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedRows(newExpanded);
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="flex flex-col items-center justify-center px-6 py-12">
          <LiquidFillLoader size={100} />
          <span className="mt-4 text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
            Loading sessions...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
          Error: {error}
        </div>
      </div>
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          {hasSearchFilter && searchTerm ? (
            <>No sessions found matching your search.</>
          ) : (
            <>No sessions found.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-2 px-6">
        <div className="text-xs text-[#475569]" style={{ fontFamily: 'Raleway, sans-serif' }}>
          Showing <span className="font-semibold">{sessions.length}</span> most recent results of{' '}
          {Number(totalSessions || 0).toLocaleString()} matches
        </div>
      </div>

      <div className="overflow-hidden rounded-lg" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'Raleway, sans-serif' }}>
            <thead className="sticky top-0 z-10 bg-gray-100">
              <tr style={{ borderBottom: `2px solid ${styles.borderColor}` }}>
                {visibleOrder.map((key) => {
                  const col = AVAILABLE_COLUMNS[key];
                  const label = col ? col.label : key;
                  const isFirst = visibleOrder[0] === key;
                  return (
                    <th
                      key={key}
                      className={`px-6 py-3 text-xs font-semibold uppercase tracking-wider bg-gray-100 ${
                        isFirst ? 'text-left' : 'text-center'
                      }`}
                      style={{ color: styles.headerText }}
                    >
                      {label}
                    </th>
                  );
                })}
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider bg-gray-100" style={{ color: styles.headerText }} />
              </tr>
            </thead>
            <tbody>
              {sessions.map((session, index) => (
                <SessionRow
                  key={`${session.sessionId}-${index}`}
                  session={session}
                  isExpanded={expandedRows.has(session.sessionId)}
                  onToggle={() => toggleRow(session.sessionId)}
                  columns={visibleOrder}
                  styles={styles}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {hasMore && !loading && !error && sessions.length > 0 && (
        <div className="mt-3">
          <div className="flex items-center justify-center">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                fontFamily: 'Raleway, sans-serif',
                backgroundColor: loadingMore ? styles.secondaryBg : styles.primaryBg,
                color: loadingMore ? styles.textSecondary : styles.textPrimary,
                border: `1px solid ${styles.borderColor}`
              }}
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionsTable;
